import { Box, Button } from '@wix/design-system'
import { useContent, useNext, usePageLoading } from '@wix/funnel-builder-lizard'
import React, { FC, useCallback, useEffect, useReducer, useRef } from 'react'

import { usePageLogger } from '../../../bi/mfi'
import { useStorageContext } from '../../../context/StorageContext/StorageContext'
import { useBrowserTabTitle } from '../../../hooks/useBrowserTabTitle'
import { ContentWithLoading } from '../../reusable/Button'
import { HeadlessTemplate } from '../../reusable/HeadlessTemplate'
import { AppItem, AppItemOnClick } from './AppItem'
import type { Content } from './Content'
import { dataHooks } from './HeadlessApps.dataHooks'
import s from './HeadlessApps.scss'
import { getBIAnswer, getInitialState, goNext, goSkip, reducer, toggleApp } from './state'

export const HeadlessApps: FC = () => {
  const { save } = useStorageContext()
  const { content } = useContent<Content>()
  const { questionKey, options } = content.question
  const [state, dispatch] = useReducer(reducer, options, getInitialState)
  const { value } = state
  const next = useNext()
  const [isPageLoading] = usePageLoading()

  useBrowserTabTitle(content.browserTabTitle)

  const pageLogger = usePageLogger()

  const handleNext = useCallback(
    () => goNext({ isPageLoading, value, questionKey, options, next, save, pageLogger }),
    [isPageLoading, value, questionKey, options, next, save, pageLogger],
  )

  const handleClick = useCallback<AppItemOnClick>(
    ({ option, index, checked }) => toggleApp({ dispatch, option, index, checked, pageLogger, questionKey }),
    [pageLogger, questionKey],
  )

  const handleSkipClick = useCallback(
    () => goSkip({ options, save, next, questionKey }),
    [options, save, next, questionKey],
  )

  const screenViewSent = useRef(false)
  useEffect(() => {
    if (!screenViewSent.current) {
      void pageLogger.screenView({ initialData: getBIAnswer({ options, value }) })
      screenViewSent.current = true
    }
  }, [pageLogger, options, value])

  const optionsNode = (
    <Box direction="vertical" flex="1" overflowY="scroll">
      {options.map((option, index) => (
        <AppItem
          key={option.optionKey}
          option={option}
          index={index}
          checked={value[option.optionKey]}
          onClick={handleClick}
          builtInLabel={content.builtInLabel}
          dataHook={dataHooks.appItem({ key: option.optionKey })}
        />
      ))}
    </Box>
  )

  return (
    <HeadlessTemplate
      isPageLoading={isPageLoading}
      pageLogger={pageLogger}
      noScreenViewBI
      stepText={content.stepText}
      title={content.title}
      subtitle={content.subtitle}
      cardTitle={content.selectTitle}
      backButton={content.backButton}
      skipButton={content.skipButton}
      onSkipClick={handleSkipClick}
      cardMarginTop="48px"
      dataHook={dataHooks.template()}
    >
      {optionsNode}
      <Box className={s.nextButtonWrapper}>
        <Button size="large" onClick={handleNext} fullWidth dataHook={dataHooks.nextButton()}>
          <ContentWithLoading isLoading={isPageLoading}>{content.nextButton}</ContentWithLoading>
        </Button>
      </Box>
    </HeadlessTemplate>
  )
}
